import { axiosInstance } from "../../../api/axiosInstance";
import { LeaveCoverage, MySubstitution, SubstitutionDay, SubstitutionNeed } from "../types";

/**
 *   GET /substitutions?date=        → SubstitutionDay   (admin)  periods needing cover, who covers, who may
 *   PUT /substitutions?date=  { assignments: [{ timetableSlotId, substituteFacultyId | null }] }
 *                                   → SubstitutionDay   (admin)  all or nothing; 409 lists every clash
 *   GET /substitutions/me           → MySubstitution[]  (faculty) own covers from today
 *   GET /leave/{id}/coverage        → LeaveCoverage     (admin)  for the Approve dialog
 */

// The backend drops nulls, false and empty lists from JSON (jackson non_empty) — put them back.
const normalizeNeed = (need: Partial<SubstitutionNeed>): SubstitutionNeed =>
  ({
    ...need,
    halfDay: need.halfDay ?? null,
    substituteFacultyId: need.substituteFacultyId ?? null,
    candidates: (need.candidates ?? []).map((c) => ({ ...c, sameSubject: Boolean(c.sameSubject), coversThisWeek: c.coversThisWeek ?? 0 })),
  }) as SubstitutionNeed;

const normalizeDay = (day: Partial<SubstitutionDay>): SubstitutionDay => ({
  date: day.date!,
  academicYear: day.academicYear ?? null,
  needs: (day.needs ?? []).map(normalizeNeed),
});

export const fetchSubstitutionDay = async (date: string): Promise<SubstitutionDay> => {
  const response = await axiosInstance.get<Partial<SubstitutionDay>>("/substitutions", { params: { date } });
  return normalizeDay(response.data);
};

export const saveSubstitutionDay = async (
  date: string,
  assignments: { timetableSlotId: number; substituteFacultyId: number | null }[]
): Promise<SubstitutionDay> => {
  const response = await axiosInstance.put<Partial<SubstitutionDay>>("/substitutions", { assignments }, { params: { date } });
  return normalizeDay(response.data);
};

export const fetchMySubstitutions = async (): Promise<MySubstitution[]> => {
  const response = await axiosInstance.get<MySubstitution[]>("/substitutions/me");
  return response.data ?? [];
};

export const fetchLeaveCoverage = async (leaveId: number): Promise<LeaveCoverage> => {
  const response = await axiosInstance.get<Partial<LeaveCoverage>>(`/leave/${leaveId}/coverage`);
  return { totalPeriods: response.data.totalPeriods ?? 0, coverable: response.data.coverable ?? 0, gaps: response.data.gaps ?? [] };
};
